import { useCallback, useState } from 'react';
import clsx from 'clsx';
import { Check, Eye, X, RotateCcw, Sparkles } from 'lucide-react';
import EmailIframePreview from './EmailIframePreview';
import { buildIncidentEmailHTML, Incident, Communication } from './EmailGeneration';
import { generateLLMExecutiveSummary, saveEmailRecipientsOnly } from '../../api/fetchIncidents'; // ✅ Use new function
import { rephrasEmailSubject, saveEmailSubject } from '../../api/incidentEmail'; // ✅ Remove saveImpactedApplications from here

interface EmailRecipient {
  email: string;
  appNumber?: string | number | null;
}

interface EmailSummaryProps {
  currentIncident: Incident;
  currentImpactedApps: string[];
  currentComms: Communication[];
  execSummary: string;
  setExecSummary: (v: string) => void;
  generateExecutiveSummary: () => void;
  loadingExecSummary: boolean;
  emailRecipients: EmailRecipient[];
  setEmailRecipients: React.Dispatch<React.SetStateAction<EmailRecipient[]>>;
  emailInput: string;
  setEmailInput: React.Dispatch<React.SetStateAction<string>>;
  emailSubject: string;
  setEmailSubject: (v: string) => void;
  emailSending: boolean;
  emailSent: boolean;
  setEmailSent: (v: boolean) => void;
  sendEmail: () => void;
  emailHtml: string;
  setEmailHtml: (v: string) => void;
  setPreviewOpen: (v: boolean) => void;
  close: () => void;
  finalOriginalNotes?: string;
  finalRephrasedNotes?: string;
  useRephrased?: boolean;
  setUseRephrased?: (v: boolean) => void;
  hideHeaderClose?: boolean;
  sendLabel?: string;
  sendingOverride?: boolean;
  onBeforeSend?: () => Promise<void>; // ✅ NEW: callback before sending
}

export default function EmailSummary({
  currentIncident,
  currentImpactedApps,
  currentComms,
  execSummary,
  setExecSummary,
  generateExecutiveSummary,
  loadingExecSummary,
  emailRecipients,
  setEmailRecipients,
  emailInput,
  setEmailInput,
  emailSubject,
  setEmailSubject,
  emailSending,
  emailSent,
  setEmailSent,
  sendEmail,
  emailHtml,
  setEmailHtml,
  setPreviewOpen,
  close,
  finalOriginalNotes,
  finalRephrasedNotes,
  useRephrased = false,
  setUseRephrased,
  hideHeaderClose = false,
  sendLabel,
  sendingOverride,
  onBeforeSend
}: EmailSummaryProps) {

  const isValidEmail = (raw: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw.trim());

  const [rephrasing, setRephrasing] = useState(false);

  const [originalSubject, setOriginalSubject] = useState(emailSubject);
  const [rephrasingSubject, setRephrasingSubject] = useState(false);
  const [subjectRephrased, setSubjectRephrased] = useState(false);
  const [subjectFocused, setSubjectFocused] = useState(false);

  const commitEmailInput = useCallback(() => {
    const raw = emailInput.trim();
    if (!raw) return;
    const parts = raw.split(/[,;\s]+/).map(p => p.trim()).filter(Boolean);
    const toAdd: EmailRecipient[] = [];
    parts.forEach(p => {
      if (isValidEmail(p) && !emailRecipients.some(r => r.email === p)) {
        toAdd.push({ email: p });
      }
    });
    
    if (toAdd.length) {
      const updatedRecipients = [...emailRecipients, ...toAdd];
      setEmailRecipients(updatedRecipients);
      
      // ✅ NEW: Save recipients immediately when manually added
      if (currentIncident) {
        (async () => {
          try {
            await saveEmailRecipientsOnly(
              currentIncident.id,
              updatedRecipients
            );
            console.log('Manually added recipients saved:', updatedRecipients);
          } catch (e) {
            console.error('Failed to save manually added recipients:', e);
          }
        })();
      }
    }
    
    setEmailInput('');
  }, [emailInput, emailRecipients, setEmailRecipients, setEmailInput, currentIncident]);

  const handleEmailInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['Enter', 'Tab', ',', ';'].includes(e.key)) {
      e.preventDefault();
      commitEmailInput();
    } else if (e.key === 'Backspace' && !emailInput) {
      setEmailRecipients(prev => prev.slice(0, -1));
    }
  };

  const removeRecipient = (addr: string) => {
    const updatedRecipients = emailRecipients.filter(r => r.email !== addr);
    setEmailRecipients(updatedRecipients);
    
    // ✅ NEW: Save ONLY recipients without triggering DL fetch
    if (currentIncident) {
      (async () => {
        try {
          await saveEmailRecipientsOnly(
            currentIncident.id,
            updatedRecipients
          );
          console.log('Recipients updated in database after removal (no DL fetch):', updatedRecipients);
        } catch (e) {
          console.error('Failed to update recipients after removal:', e);
        }
      })();
    }
  };

  const effectiveFinalNotes = useRephrased && finalRephrasedNotes
    ? finalRephrasedNotes
    : (finalOriginalNotes || '');

  const openPreview = () => {
    const html = buildIncidentEmailHTML(
      currentIncident,
      currentImpactedApps,
      currentComms,
      execSummary,
      effectiveFinalNotes || undefined,
      true
    );
    setEmailHtml(html);
    setPreviewOpen(true);
  };

  const copyHtml = () => {
    const h = buildIncidentEmailHTML(
      currentIncident,
      currentImpactedApps,
      currentComms,
      execSummary,
      effectiveFinalNotes || undefined
    );
    setEmailHtml(h);
    navigator.clipboard.writeText(h).catch(() => {});
  };

  const handleRephrase = async () => {
    if (rephrasing) return;
    setRephrasing(true);
    try {
      const r = await generateLLMExecutiveSummary(execSummary);
      if (r.success && r.suggested_update) {
        setExecSummary(r.suggested_update.trim());
      }
    } catch (e) {
      console.warn('Rephrase failed', e);
    } finally {
      setRephrasing(false);
    }
  };

  const handleSubjectBlur = async () => {
    const trimmedSubject = emailSubject.trim();
    
    if (!trimmedSubject || trimmedSubject === originalSubject) {
      setSubjectFocused(false);
      return;
    }
    
    setSubjectFocused(false);
    
    try {
      await saveEmailSubject(currentIncident.id, trimmedSubject);
      console.log('Subject saved to DB:', trimmedSubject);
      setOriginalSubject(trimmedSubject);
    } catch (e) {
      console.warn('Failed to save subject:', e);
    }
  };

  const handleRephraseSubject = async () => {
    if (rephrasingSubject) return;
    
    if (!subjectRephrased) {
      setOriginalSubject(emailSubject);
    }

    setRephrasingSubject(true);
    try {
      const result = await rephrasEmailSubject(
        emailSubject,
        currentIncident.id,
        currentIncident.severity
      );
      if (result.success && result.rephrased_subject) {
        setEmailSubject(result.rephrased_subject);
        setSubjectRephrased(true);
      }
    } catch (e) {
      console.warn('Subject rephrase failed', e);
    } finally {
      setRephrasingSubject(false);
    }
  };

  const handleRevertSubject = async () => {
    const revertedSubject = originalSubject;
    setEmailSubject(revertedSubject);
    setSubjectRephrased(false);
    
    try {
      await saveEmailSubject(currentIncident.id, revertedSubject);
    } catch (e) {
      console.warn('Failed to save reverted subject:', e);
    }
  };

  const handleSend = async () => {
    // ✅ Save current subject to DB before sending (ensure it's persisted)
    const trimmedSubject = emailSubject.trim();
    if (trimmedSubject) {
      try {
        await saveEmailSubject(currentIncident.id, trimmedSubject);
        setOriginalSubject(trimmedSubject); // Update original to match saved
      } catch (e) {
        console.warn('Failed to save subject before send:', e);
      }
    }
  
    // ✅ Call pre-send hook to update circumvention time
    if (onBeforeSend) {
      await onBeforeSend();
    }
    
    sendEmail();
    setEmailSent(true);
  };

  const APPROVAL_NEED = true;

  return (
    <div className="mt-3 border border-border/50 rounded-xl bg-background/40 dark:bg-background/30 p-3 flex flex-col gap-2 flex-1 min-h-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between shrink-0">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Send Summary as Email 
        </span>
        {!hideHeaderClose && (
          <button
            onClick={close}
            className="text-[10px] px-2 py-0.5 rounded-md hover:bg-background/60 transition-colors"
            aria-label="Close email form"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Recipients */}
      <div className="flex flex-col gap-1 shrink-0">
        <label className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
          To
        </label>
        <div
          className="flex flex-wrap items-center gap-1 px-2 py-1 rounded-md min-h-[34px] max-h-24 overflow-y-auto bg-background/60 dark:bg-background/40 border border-border/50 focus-within:ring-1 focus-within:ring-[#18E78F]/50 custom-scrollbar incidents-scroll"
        >
          {emailRecipients.map(r => (
            <span
              key={r.email}
              className="group flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-md bg-[#18E78F]/15 border border-[#18E78F]/40 text-[#18E78F] text-[10px]"
              title={r.appNumber ? `From ${r.appNumber}` : ''}
            >
              {r.email}
              <button
                type="button"
                onClick={() => removeRecipient(r.email)}
                className="p-0.5 rounded hover:bg-[#18E78F]/25"
                aria-label={`Remove ${r.email}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <input
            value={emailInput}
            onChange={e => setEmailInput(e.target.value)}
            onBlur={commitEmailInput}
            onKeyDown={handleEmailInputKeyDown}
            placeholder={emailRecipients.length ? '' : 'Add recipient and press Enter…'}
            className="flex-1 min-w-[120px] bg-transparent outline-none text-[11px] py-0.5"
          />
        </div>
        <div className="text-[9px] text-muted-foreground/60">
          Enter / Tab / , / ; to add. Invalid addresses ignored.
        </div>
      </div>

      {/* Subject with Rephrase Button */}
      <div className="flex flex-col gap-1 shrink-0">
        <label className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
          Subject
        </label>
        <div className="relative group">
          <textarea
            placeholder="Subject"
            value={emailSubject}
            onChange={e => setEmailSubject(e.target.value)}
            onFocus={() => setSubjectFocused(true)}
            onBlur={handleSubjectBlur}
            rows={subjectFocused ? 3 : 1}
            className={clsx(
              "w-full text-[11px] px-2 py-1.5 pr-20 rounded-md resize-none",
              "bg-background/60 dark:bg-background/40 border border-border/50",
              "focus:outline-none focus:ring-1 focus:ring-[#18E78F]/50",
              "transition-all duration-200 ease-in-out",
              "overflow-y-auto custom-scrollbar incidents-scroll",
              subjectFocused && "min-h-[4.5rem] max-h-[6rem]"
            )}
          />
          <div className={clsx(
            "absolute right-1 flex items-center gap-1 transition-all duration-200",
            subjectFocused ? "top-1" : "top-1/2 -translate-y-1/2"
          )}>
            <button
              onClick={handleRephraseSubject}
              disabled={rephrasingSubject}
              className={clsx(
                "p-1 rounded-md transition-all duration-150",
                "border border-border/40 bg-background/80 backdrop-blur-sm",
                "hover:bg-[#18E78F]/15 hover:border-[#18E78F]/50",
                "disabled:opacity-40 disabled:cursor-not-allowed",
                "group/ai"
              )}
              title="Rephrase subject using AI"
            >
              {rephrasingSubject ? (
                <div className="w-3.5 h-3.5 border-2 border-[#18E78F]/30 border-t-[#18E78F] rounded-full animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5 text-[#18E78F] group-hover/ai:animate-pulse" />
              )}
            </button>
            {subjectRephrased && (
              <button
                onClick={handleRevertSubject}
                className={clsx(
                  "p-1 rounded-md transition-all duration-150",
                  "border border-orange-500/40 bg-background/80 backdrop-blur-sm",
                  "hover:bg-orange-500/15 hover:border-orange-500/60",
                  "group/revert"
                )}
                title="Revert to original subject"
              >
                <RotateCcw className="w-3.5 h-3.5 text-orange-500 group-hover/revert:rotate-[-180deg] transition-transform duration-300" />
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between text-[9px] text-muted-foreground/60">
          <span>Click to expand • AI enhance available</span>
          {subjectRephrased && (
            <span className="flex items-center gap-1 text-[#18E78F] font-medium">
              <Sparkles className="w-2.5 h-2.5" />
              AI Enhanced
            </span>
          )}
        </div>
      </div>

      {/* Executive summary block grows */}
      <div className="flex flex-col flex-1 min-h-[260px]">
        <div className="flex items-center justify-between shrink-0">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Executive Summary
          </span>
        </div>
        <textarea
          value={execSummary}
          onChange={e => setExecSummary(e.target.value)}
          className="flex-1 min-h-0 resize-none rounded-md bg-background/60 dark:bg-background/40 border border-border/50 focus:outline-none focus:ring-1 focus:ring-[#18E78F]/50 text-[11px] leading-relaxed p-2"
          placeholder="Executive summary (auto-generated). You can edit before sending."
        />
      </div>

      <div className="flex items-center justify-between shrink-0">
        <span className="text-[10px] text-muted-foreground/60">
          {emailSent 
            ? "✓ Email sent! You can close this form or send another email."
            : "Preview to inspect formatted message."}
        </span>
        <div className="flex items-center gap-2">
          {emailSent && (
            <span className="flex items-center gap-1 text-[10px] text-[#18E78F] font-medium animate-in fade-in zoom-in duration-300">
              <Check className="w-3.5 h-3.5" /> {/* ✅ Slightly larger */}
              Sent
            </span>
          )}
          <button
            type="button"
            onClick={openPreview}
            className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border border-border/50 hover:bg-[#18E78F]/15 transition-colors"
            disabled={!emailRecipients.length}
          >
            <Eye className="w-3.5 h-3.5" />
            Preview
          </button>
          <button
            onClick={handleSend}
            disabled={
              (sendingOverride ?? emailSending) ||
              !emailRecipients.length ||
              !emailSubject.trim() ||
              emailSent // ✅ Disable after sending to prevent duplicate sends
            }
            className="text-[10px] px-3 py-1 rounded-md border border-[#18E78F]/50 bg-[#18E78F]/10 hover:bg-[#18E78F]/20 font-medium disabled:opacity-40 transition-colors"
          >
            {(sendingOverride ?? emailSending)
              ? 'Sending…'
              : emailSent
              ? 'Sent ✓' // ✅ Show checkmark in button
              : (sendLabel || 'Send')}
          </button>
        </div>
      </div>
    </div>
  );
}